import { maxScrollTop, targetScrollTop, scrollToElement } from 'scroll-utils';

class SectionProgress extends HTMLElement {
  connectedCallback() {
    const container = document.getElementById(this.getAttribute('sections-container'));
    if (!container) return;

    // ponytail: no height/visibility filter here — Shopify only ever emits enabled sections
    // into content_for_layout, so every direct child is a real section. Filtering by
    // offsetHeight was racy (sections that lay out late briefly measure 0) and caused pins
    // to point at the wrong section once one got dropped. We do exclude our own section
    // wrapper (it'd otherwise show up as a pin pointing at itself) and any "scroll to next
    // section" button sections — they're navigation controls, not content worth a pin.
    this.sections = Array.from(container.querySelectorAll(':scope > .shopify-section')).filter(
      (section) => !section.contains(this) && !section.querySelector('next-section-button')
    );
    if (this.sections.length < 2) return;

    this.fill = this.querySelector('.section-progress__fill');
    this.pinsContainer = this.querySelector('.section-progress__pins');
    const hoverLabel = this.getAttribute('hover-label') || '';
    this.activeIndex = 0;

    this.sections.forEach((section, index) => {
      const pin = document.createElement('button');
      pin.type = 'button';
      pin.className = 'section-progress__pin';
      pin.setAttribute('aria-label', `Go to section ${index + 1} of ${this.sections.length}`);
      pin.addEventListener('click', () => {
        // Set active immediately instead of waiting for scroll geometry to catch up — for the
        // last section(s), there isn't enough page left below them for their target scroll
        // position to ever be reached, so geometry-based detection alone would never agree.
        this.setActive(index);
        scrollToElement(section);
      });

      if (hoverLabel) {
        const tooltip = document.createElement('span');
        tooltip.className = 'section-progress__tooltip';
        tooltip.textContent = hoverLabel;
        pin.appendChild(tooltip);
      }

      this.pinsContainer.appendChild(pin);
    });
    this.pins = Array.from(this.pinsContainer.children);

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  setActive(index) {
    this.activeIndex = index;
    this.pins.forEach((pin, i) => pin.classList.toggle('is-active', i === index));
  }

  onScroll() {
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.update();
    });
  }

  update() {
    // Pins sit at even spacing on the track regardless of each section's actual height, so
    // raw scroll-% doesn't land at the same place as the active pin. Instead, interpolate
    // between pin positions directly: the fill always reaches a pin exactly when it goes active.
    const targets = this.sections.map((section) => targetScrollTop(section));

    let activeIndex = 0;
    targets.forEach((target, index) => {
      if (window.scrollY >= target - 1) activeIndex = index;
    });
    this.setActive(activeIndex);

    const segmentStart = targets[activeIndex];
    const segmentEnd = activeIndex + 1 < targets.length ? targets[activeIndex + 1] : maxScrollTop();
    const segmentProgress =
      segmentEnd > segmentStart
        ? Math.min(1, Math.max(0, (window.scrollY - segmentStart) / (segmentEnd - segmentStart)))
        : 1;

    const pinFraction = (activeIndex + segmentProgress) / (this.sections.length - 1);
    this.fill.style.height = `${Math.min(100, Math.max(0, pinFraction * 100))}%`;
  }
}

customElements.define('section-progress', SectionProgress);
